import type { AdminDeckDto } from '@lunary/api-client'
import { CARDS } from '@lunary/tarot-data'
import { toRoman } from '@lunary/app-core/roman'
import { CardTextPreview, THREE_TEXT_FIELDS } from '@lunary/ui-web/card-text'
import type { CardTextValue, ThreeTextKey } from '@lunary/ui-web/card-text'
import { CardFace } from '../../shared/ui/card-face'
import expert from '../../shared/ui/expert.module.css'
import { changedFields, usePublishConfirm } from '../../shared/ui/publish-confirm'
import { deckErrorText } from './errors'
import { useResetThreeCardText, useSaveThreeCardText, useThreeCardTexts } from './queries'
import styles from './decks.module.css'

const SUIT: Record<string, string> = {
  wands: 'Жезлы',
  cups: 'Кубки',
  swords: 'Мечи',
  pentacles: 'Пентакли',
}

export function ThreeCardForm({ deck, cardId }: { deck: AdminDeckDto; cardId: string }) {
  const card = CARDS.find((c) => c.id === cardId)
  const texts = useThreeCardTexts()
  const save = useSaveThreeCardText()
  const reset = useResetThreeCardText()
  const stored = texts.data?.items.find((t) => t.card_id === cardId)
  const { ask, confirmed, dialog } = usePublishConfirm()
  if (!card) return null
  const image = deck.cards.find((c) => c.card_id === cardId)
  const eyebrow = card.arcana === 'major' ? `Старший аркан ${toRoman(card.number)}` : `Младший аркан · ${card.suit ? SUIT[card.suit] : ''}`
  const value = { body: stored?.body ?? '', hint: stored?.hint ?? '', advice: stored?.advice ?? '' }
  const failed = save.error ?? reset.error ?? texts.error
  const notAffected: readonly [string, string] = ['Не затронет', 'уже выбранные наборы дней, их текст правится отдельно']
  const onSave = async (next: CardTextValue<ThreeTextKey>) => {
    await confirmed({
      title: 'Изменить описание для всех колод?',
      text: `Это описание «${card.name}» общее для всех колод «Трёх карт». Оно подставится в будущие наборы с этой картой.`,
      confirmLabel: 'Опубликовать',
      facts: [['Меняется', changedFields(THREE_TEXT_FIELDS, value, next)], notAffected],
    })
    return save.mutateAsync({ cardId, ...next })
  }
  const onReset = async () => {
    const ok = await ask({
      title: 'Вернуть исходное описание?',
      text: `Описание «${card.name}» для всех колод «Трёх карт» вернётся к исходному тексту.`,
      confirmLabel: 'Вернуть и опубликовать',
      facts: [notAffected],
    })
    if (ok) reset.mutate(cardId)
  }

  return (
    <section className={`${expert.panel} ${styles.cardForm}`} aria-labelledby="card-form-title">
      <div>
        <span className={styles.eyebrow}>{eyebrow}</span>
        <h2 id="card-form-title">{card.name}</h2>
      </div>
      <b className={styles.formTitle}>Описание карты для «Трёх карт»</b>
      {texts.isPending ? (
        <div className="spinner" aria-label="Загрузка" />
      ) : (
        <CardTextPreview
          fields={THREE_TEXT_FIELDS}
          value={value}
          face={<CardFace src={image?.url ?? image?.thumb_url} number={card.arcana === 'major' ? card.number : null} name={card.name} size="lg" />}
          eyebrow="Людмила о карте"
          name={card.name}
          status={stored ? (stored.is_default ? 'default' : 'changed') : null}
          saving={save.isPending}
          saved={save.isSuccess || reset.isSuccess}
          error={failed ? deckErrorText(failed) : null}
          saveLabel="Опубликовать…"
          savedLabel="Опубликовано"
          onSave={onSave}
          onReset={() => void onReset()}
          resetting={reset.isPending}
        />
      )}
      {dialog}
      <p className={styles.hint}>Общее для всех колод «Трёх карт». Картинку меняют и убирают в сетке слева.</p>
    </section>
  )
}
